// frontend/src/utils/pwaInstall.js

import { useCallback, useRef, useSyncExternalStore } from 'react';

import { useIsStandalonePwa } from './pwaDisplayMode';
import pushSubscriptionService from '../services/pushSubscriptionService';
import analytics from '../services/analytics';

/**
 * The app's SINGLE owner of the `beforeinstallprompt` event.
 *
 * WHY THIS MODULE EXISTS (do not "simplify" it back into per-component
 * listeners):
 *
 *   1. `beforeinstallprompt` fires ONCE per page load, and the event object it
 *      hands over can be replayed exactly ONCE. Whichever listener calls
 *      `preventDefault()` and keeps the event owns the only install affordance
 *      the page will ever have.
 *   2. Two separate surfaces need it - the top install banner
 *      (components/InstallPrompt.js) and the push-notifications announcement
 *      dialog (components/dashboard/PushNotificationsAnnounce.js). If each kept
 *      its own `useState` copy, the one that mounted second would hold `null`
 *      forever and its Install button would be permanently dead.
 *   3. The event can fire BEFORE React mounts (Chrome fires it as soon as the
 *      manifest + service worker are evaluated). A listener attached inside a
 *      `useEffect` therefore misses it on a cold load. This module registers at
 *      module-evaluation time - it is pulled in by App.js's static import chain,
 *      so the listener is in place before React renders anything.
 *
 *      RESIDUAL GAP, deliberately accepted: "before React renders" is NOT
 *      "before page load". This listener only exists once the main bundle has
 *      been parsed and executed, so a `beforeinstallprompt` fired earlier than
 *      that is lost outright, with no recovery - Chrome does not replay it on
 *      demand. Closing it structurally needs a few lines of inline capture in
 *      public/index.html that stash the event on `window` for this module to
 *      adopt. That file is outside this module's reach; until it is done, a
 *      cold load on a slow connection can legitimately show no install
 *      affordance at all, which is why every consumer is written to degrade to
 *      "no install available" rather than to assume one is coming.
 *
 * The store is deliberately tiny: one nullable event, a Set of subscribers, and
 * a boolean snapshot. Consumers never touch the raw event - they ask whether an
 * install can be prompted and call `promptInstall()`.
 *
 * GRACEFUL DEGRADATION: iOS Safari and Firefox never fire this event at all,
 * and there is no `window` during SSR / a jsdom-less test. Every export degrades
 * to "no install available" instead of throwing, so a caller can render its
 * adaptive fallback (the instructions dialog, or nothing) with no capability
 * sniffing of its own.
 */

/** Outcomes of `promptInstall()`. Exported so callers avoid magic strings. */
export const INSTALL_OUTCOME = Object.freeze({
  /** The user accepted the browser's install prompt. */
  ACCEPTED: 'accepted',
  /** The browser's prompt was shown and the user declined it. */
  DISMISSED: 'dismissed',
  /**
   * No prompt could be shown at all: the event never fired, was already
   * consumed, or the browser refused to replay it. Callers should close
   * quietly - this is not an error the user did anything to cause.
   */
  UNAVAILABLE: 'unavailable',
});

/** The captured, not-yet-consumed `BeforeInstallPromptEvent`, or null. */
let deferredEvent = null;

/**
 * True from the moment the event is consumed until the user's choice resolves.
 *
 * It exists so availability does not blink to false the instant `prompt()` is
 * called: the native prompt can stay on screen for seconds, and on DESKTOP the
 * page underneath remains interactive, so a false snapshot would visibly retire
 * the very button the user just pressed while its prompt is still open.
 */
let promptInFlight = false;

/** Subscribers to availability changes (React `useSyncExternalStore` callbacks). */
const listeners = new Set();

const notify = () => {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      /* one bad subscriber must not starve the others */
    }
  });
};

/**
 * Replace the stored event and wake every subscriber. No-op when the value is
 * unchanged, so a duplicate `appinstalled` cannot cause a pointless re-render.
 *
 * @param {Event|null} event
 */
const setDeferredEvent = (event) => {
  if (deferredEvent === event) return;
  deferredEvent = event;
  notify();
};

/**
 * Should an install affordance be offered right now?
 *
 * False on iOS Safari / Firefox (the event never fires), once the event has
 * been consumed, and after the app has been installed. Deliberately stays TRUE
 * while a prompt is in flight - see `promptInFlight`. A caller that finds it
 * true and still gets `UNAVAILABLE` back is the re-entrant case, which callers
 * must treat as "close quietly", exactly like every other unavailable outcome.
 *
 * @returns {boolean}
 */
export const canPromptInstall = () => deferredEvent !== null || promptInFlight;

/**
 * Replay the captured event and report what the user chose.
 *
 * The event is consumed BEFORE `prompt()` is called, not after `userChoice`
 * resolves. That window is otherwise seconds long - the whole time the native
 * prompt is up, during which our JS keeps running - and a second caller
 * entering it would call `prompt()` on an already-used event, which Chrome
 * rejects with `InvalidStateError`. Clearing first makes double consumption
 * structurally impossible rather than merely unlikely. `promptInFlight` keeps
 * the UI stable across that same window.
 *
 * Never throws.
 *
 * @returns {Promise<'accepted'|'dismissed'|'unavailable'>}
 */
export const promptInstall = async () => {
  const event = deferredEvent;
  if (promptInFlight || !event || typeof event.prompt !== 'function') {
    return INSTALL_OUTCOME.UNAVAILABLE;
  }

  deferredEvent = null;
  promptInFlight = true;
  // Net availability is unchanged (true -> true), so subscribers re-read an
  // identical snapshot and React skips the render. Notifying anyway keeps this
  // the single place that mutates the store.
  notify();

  try {
    event.prompt();
    const choice = await event.userChoice;
    return choice?.outcome === 'accepted'
      ? INSTALL_OUTCOME.ACCEPTED
      : INSTALL_OUTCOME.DISMISSED;
  } catch {
    // The browser refused to replay the event (stale gesture, already used).
    return INSTALL_OUTCOME.UNAVAILABLE;
  } finally {
    // The event is spent either way: availability drops here, which is what
    // retires every install affordance in the app.
    promptInFlight = false;
    notify();
  }
};

/**
 * Subscribe to availability changes.
 *
 * @param {() => void} listener
 * @returns {() => void} unsubscribe
 */
const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// Delegated (not re-derived) so the hook and the imperative check can never
// disagree about what "available" means.
const getSnapshot = () => canPromptInstall();

// No `window` during SSR, and no install prompt there either.
const getServerSnapshot = () => false;

/**
 * React binding for the store: `true` while an install can be prompted.
 *
 * `useSyncExternalStore` rather than the `useState` + `useEffect` pair used by
 * the sibling `pwaDisplayMode.js`, because here the value can change in the gap
 * between render and effect commit - React re-reads the snapshot right after
 * subscribing, so a component that mounts in that gap cannot miss the event.
 *
 * @returns {boolean}
 */
export const useInstallAvailability = () =>
  useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

// ── The one install ACTION ─────────────────────────────────────────────────
// Everything above is state; this is the behaviour every install entry point
// in the app shares. It lived as three near-identical copies (the navbar menu,
// the notification settings block, the push announcement dialog) while a
// fourth surface - the profession-identified dialog - was simply missed and
// went on opening the manual instructions even for a browser that could
// install in one tap. Copies are exactly what this hook exists to prevent: the
// branch deciding what happens when the browser CANNOT install us is the one
// iOS users depend on, because the manual "share -> add to home screen" flow
// is the ONLY route to an installed app on that platform.

/**
 * The adaptive "install the app" action.
 *
 * Resolution order, which every call site already agreed on:
 *   1. The browser can install us itself -> trigger the REAL installer.
 *      Showing a hand-written how-to to someone whose browser has a working
 *      one-tap install is the thing these entries used to get wrong.
 *   2. Anything else (iOS Safari, Firefox, a desktop that never fired the
 *      event, or an event another surface already consumed) -> `onUnavailable`,
 *      which is normally the shared InstallInstructionsDialog. Passing no
 *      handler is a valid choice for a surface that must not stack a second
 *      modal on itself - the push announcement dialog does exactly that.
 *
 * Outcomes:
 *   - ACCEPTED    -> subscribe to push, fire-and-forget. An installed app with
 *                    no push subscription delivers nothing. Never surfaced: a
 *                    failure there is not the user's problem.
 *   - DISMISSED   -> nothing visible. Declining is a decision, not a failure,
 *                    and answering it with a how-to is nagging.
 *   - UNAVAILABLE -> `onUnavailable`. The event is spent, but the user asked to
 *                    install and something must answer - the browser's own menu
 *                    still installs.
 *
 * GESTURE CHAIN: `install` reaches `promptInstall()` with NO await ahead of it.
 * A single await before that point costs the user gesture and the browser
 * refuses to show the prompt at all. Callers must keep their own side of that
 * chain synchronous too - close a menu with a state setter, never `await`
 * anything, then call `install()`.
 *
 * RE-ENTRANCY: on desktop the page stays interactive behind the native prompt,
 * so a second click is reachable. It is ignored outright - it must NOT fall
 * through to `onUnavailable`, which would pop a how-to over a live prompt. A
 * ref, not state: it has to be readable synchronously inside the same click.
 *
 * @param {object} [handlers]
 * @param {() => void} [handlers.onUnavailable] no native install happened and
 *   the user needs another route (usually: open the instructions dialog).
 * @param {() => void} [handlers.onSettled] runs on every handled click once the
 *   outcome is known, whatever it is - for a surface that must close itself
 *   either way. NOT called for an ignored re-entrant click.
 * @returns {{canOfferInstall: boolean, install: () => Promise<void>}}
 *   `canOfferInstall` is for surfaces that only render the CTA where pressing
 *   it can install something; surfaces with a working fallback ignore it and
 *   render unconditionally.
 */
export const useInstallAction = ({ onUnavailable, onSettled } = {}) => {
  // Both halves are live: `beforeinstallprompt` can arrive after the surface
  // has rendered, and the display mode flips the moment the app is installed.
  const isStandalone = useIsStandalonePwa();
  const canPrompt = useInstallAvailability();
  const canOfferInstall = canPrompt && !isStandalone;

  const installingRef = useRef(false);

  const install = useCallback(async () => {
    if (!canOfferInstall) {
      onSettled?.();
      onUnavailable?.();
      return;
    }

    if (installingRef.current) return;
    installingRef.current = true;
    try {
      const outcome = await promptInstall();
      onSettled?.();

      if (outcome === INSTALL_OUTCOME.ACCEPTED) {
        // 'install' - the auto-subscribe that follows an accepted install, not
        // a Settings toggle. Instrumentation only; the call is unchanged.
        pushSubscriptionService.subscribe('install').catch(() => {});
        return;
      }
      if (outcome === INSTALL_OUTCOME.DISMISSED) return;

      onUnavailable?.();
    } finally {
      installingRef.current = false;
    }
  }, [canOfferInstall, onUnavailable, onSettled]);

  return { canOfferInstall, install };
};

// ── Install-banner holds ───────────────────────────────────────────────────
// Same job as the store above, one level up: the app's install affordances are
// GLOBAL and must not stack. A surface that owns the screen for a moment - the
// dashboard's push announcement dialog is the one that does today - takes a
// hold, and the top install banner stands down for as long as any hold is out.
//
// A counter, not a boolean, so two holders cannot release each other's claim,
// and each `holdInstallBanner()` hands back a release function that is safe to
// call twice (React 18+ StrictMode runs effect cleanups an extra time in dev).
// This is a real signal rather than a timing hack: the banner re-arms when the
// last hold clears, so it does not depend on one component's delay being larger
// than another's.

let holdCount = 0;

/**
 * Suppress the install banner until the returned function is called.
 *
 * @returns {() => void} release - idempotent
 */
export const holdInstallBanner = () => {
  holdCount += 1;
  notify();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holdCount = Math.max(0, holdCount - 1);
    notify();
  };
};

const getHoldSnapshot = () => holdCount > 0;

/**
 * `true` while any surface has asked the install banner to stand down.
 *
 * @returns {boolean}
 */
export const useInstallBannerHeld = () =>
  useSyncExternalStore(subscribe, getHoldSnapshot, getServerSnapshot);

/**
 * Attach the one listener pair, at module-evaluation time. Guarded so importing
 * this module outside a browser (SSR, a node-side test) is inert rather than a
 * ReferenceError.
 */
const registerListeners = () => {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
    return;
  }

  window.addEventListener('beforeinstallprompt', (event) => {
    // Suppress Chrome's own mini-infobar; the app presents its own affordances.
    if (typeof event.preventDefault === 'function') event.preventDefault();
    setDeferredEvent(event);
  });

  // Installed by any route (our button, the browser's menu, another tab): the
  // stored event is spent and every Install affordance must retire itself.
  //
  // This listener is also the app's ONLY honest install signal - it fires for
  // the browser-menu install too, which `promptInstall()` never sees - so the
  // analytics event belongs here rather than on the ACCEPTED outcome. Wrapped
  // because a throw here would skip setDeferredEvent(null) and strand every
  // install affordance in the "still available" state inside the installed app.
  window.addEventListener('appinstalled', () => {
    try {
      analytics.trackEvent('pwa_installed');
    } catch {
      /* instrumentation must never break the install path */
    }
    setDeferredEvent(null);
  });
};

registerListeners();
